import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { subscriptionPlanInput, type Paginated } from '@barbeiropro/contracts';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataColumn } from '@/components/shared/data-table';
import { Field } from '@/components/shared/field';
import { FormModal } from '@/components/shared/form-modal';
import { MaskedInput } from '@/components/shared/masked-input';
import { useTableParams } from '@/components/shared/use-table-params';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { api, errorMessage } from '@/lib/api';
import { centsToInput, money, parseMoney } from '@/lib/format';
import { useServiceOptions, type Plan } from './api';
import { applyServerError, zodForm } from './lib';

interface Values {
  name: string;
  description: string;
  kind: 'recurring' | 'package';
  price: string;
  billingIntervalMonths: string;
  validityDays: string;
  usesIncluded: string;
  serviceIds: string[];
  active: boolean;
}
const empty: Values = { name: '', description: '', kind: 'package', price: '0,00', billingIntervalMonths: '1', validityDays: '60', usesIncluded: '4', serviceIds: [], active: true };
const num = (s: string) => (s.trim() === '' ? null : Number(s));
const toPayload = (v: Values) => ({
  name: v.name,
  description: v.description,
  kind: v.kind,
  priceCents: parseMoney(v.price),
  billingIntervalMonths: v.kind === 'recurring' ? num(v.billingIntervalMonths) : null,
  validityDays: v.kind === 'package' ? num(v.validityDays) : null,
  usesIncluded: num(v.usesIncluded),
  serviceIds: v.serviceIds,
  active: v.active,
});

function PlanModal({ open, onOpenChange, plan }: { open: boolean; onOpenChange: (v: boolean) => void; plan: Plan | null }) {
  const qc = useQueryClient();
  const services = useServiceOptions();
  const form = useForm<Values>({ resolver: zodForm<Values>(subscriptionPlanInput, toPayload, { priceCents: 'price' }), defaultValues: empty });
  const { errors } = form.formState;
  const kind = form.watch('kind');
  useEffect(() => {
    if (!open) return;
    form.reset(
      plan
        ? {
            name: plan.name,
            description: plan.description ?? '',
            kind: plan.kind,
            price: centsToInput(plan.priceCents),
            billingIntervalMonths: String(plan.billingIntervalMonths ?? 1),
            validityDays: String(plan.validityDays ?? 60),
            usesIncluded: plan.usesIncluded != null ? String(plan.usesIncluded) : '',
            serviceIds: plan.serviceIds,
            active: plan.active,
          }
        : empty,
    );
  }, [open, plan]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: (v: Values) => {
      const body = subscriptionPlanInput.parse(toPayload(v));
      return plan ? api.put(`/subscription-plans/${plan.id}`, body) : api.post('/subscription-plans', body);
    },
    onSuccess: () => {
      toast.success('Plano salvo');
      qc.invalidateQueries({ queryKey: ['loyalty', 'plans'] });
      onOpenChange(false);
    },
    onError: (e) => applyServerError(form, e, { priceCents: 'price' }),
  });

  return (
    <FormModal
      open={open}
      onOpenChange={onOpenChange}
      title={plan ? 'Editar plano' : 'Novo plano ou pacote'}
      description="Assinaturas só ficam ativas depois que o pagamento é registrado no caixa."
      footer={
        <>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Voltar
          </Button>
          <Button type="submit" form="plan-form" disabled={save.isPending}>
            {save.isPending ? 'Salvando…' : 'Salvar plano'}
          </Button>
        </>
      }
    >
      <form id="plan-form" className="grid gap-4" noValidate onSubmit={form.handleSubmit((v) => save.mutate(v))}>
        <Field id="plan-name" label="Nome" error={errors.name?.message}>
          <Input id="plan-name" autoFocus {...form.register('name')} />
        </Field>
        <Field id="plan-desc" label="Descrição (opcional)" error={errors.description?.message}>
          <Textarea id="plan-desc" rows={2} maxLength={1000} {...form.register('description')} />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field id="plan-kind" label="Tipo">
            <Controller
              control={form.control}
              name="kind"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger id="plan-kind" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="package">Pacote (usos com validade)</SelectItem>
                    <SelectItem value="recurring">Plano recorrente</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </Field>
          <Field id="plan-price" label="Preço" error={errors.price?.message}>
            <MaskedInput mask="money" id="plan-price" {...form.register('price')} />
          </Field>
          {kind === 'recurring' ? (
            <Field id="plan-interval" label="Cobrança a cada (meses)" error={errors.billingIntervalMonths?.message}>
              <Input id="plan-interval" inputMode="numeric" {...form.register('billingIntervalMonths')} />
            </Field>
          ) : (
            <Field id="plan-validity" label="Validade (dias)" error={errors.validityDays?.message}>
              <Input id="plan-validity" inputMode="numeric" {...form.register('validityDays')} />
            </Field>
          )}
          <Field
            id="plan-uses"
            label={kind === 'recurring' ? 'Usos por ciclo' : 'Quantidade de usos'}
            hint={kind === 'recurring' ? 'Deixe em branco para uso ilimitado.' : undefined}
            error={errors.usesIncluded?.message}
          >
            <Input id="plan-uses" inputMode="numeric" {...form.register('usesIncluded')} />
          </Field>
        </div>
        <fieldset className="grid gap-2">
          <legend className="mb-1 text-sm font-medium">Serviços cobertos</legend>
          <Controller
            control={form.control}
            name="serviceIds"
            render={({ field }) => (
              <div className="grid max-h-44 gap-2 overflow-y-auto rounded-lg border p-3">
                {services.isLoading && <p className="text-sm text-muted-foreground">Carregando serviços…</p>}
                {services.data?.length === 0 && <p className="text-sm text-muted-foreground">Cadastre serviços primeiro.</p>}
                {services.data?.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={field.value.includes(s.id)}
                      onCheckedChange={(c) => field.onChange(c ? [...field.value, s.id] : field.value.filter((x) => x !== s.id))}
                    />
                    {s.name} <span className="tabular text-muted-foreground">{money(s.priceCents)}</span>
                  </label>
                ))}
              </div>
            )}
          />
          {errors.serviceIds?.message && (
            <p role="alert" className="text-xs text-destructive">
              {errors.serviceIds.message as string}
            </p>
          )}
        </fieldset>
        <div className="flex items-center gap-3">
          <Controller control={form.control} name="active" render={({ field }) => <Switch id="plan-active" checked={field.value} onCheckedChange={field.onChange} />} />
          <label htmlFor="plan-active" className="text-sm">
            Disponível para venda
          </label>
        </div>
      </form>
    </FormModal>
  );
}

export function PlansTab() {
  const qc = useQueryClient();
  const params = useTableParams({ sort: 'name' });
  const [editing, setEditing] = useState<Plan | null>(null);
  const [open, setOpen] = useState(false);
  const list = useQuery({
    queryKey: ['loyalty', 'plans', 'list', params.query],
    queryFn: () => api.get<Paginated<Plan>>('/subscription-plans', params.query),
    placeholderData: (p) => p,
  });
  const remove = async (p: Plan) => {
    try {
      await api.del(`/subscription-plans/${p.id}`);
      toast.success('Plano excluído');
      qc.invalidateQueries({ queryKey: ['loyalty', 'plans'] });
    } catch (e) {
      toast.error(errorMessage(e));
      throw e;
    }
  };
  const columns: DataColumn<Plan>[] = [
    {
      id: 'name',
      header: 'Plano',
      meta: { sortKey: 'name' },
      cell: ({ row }) => (
        <div className="min-w-0">
          <p className="truncate font-medium">{row.original.name}</p>
          <p className="text-xs text-muted-foreground">{row.original.kind === 'recurring' ? `A cada ${row.original.billingIntervalMonths} mês(es)` : `Validade de ${row.original.validityDays} dias`}</p>
        </div>
      ),
    },
    { id: 'uses', header: 'Usos', cell: ({ row }) => <span className="tabular">{row.original.usesIncluded ?? 'Ilimitado'}</span> },
    { id: 'priceCents', header: 'Preço', meta: { sortKey: 'priceCents', align: 'right' }, cell: ({ row }) => money(row.original.priceCents) },
    { id: 'status', header: 'Situação', cell: ({ row }) => <Badge variant={row.original.active ? 'secondary' : 'outline'}>{row.original.active ? 'À venda' : 'Inativo'}</Badge> },
    {
      id: 'actions',
      header: () => <span className="sr-only">Ações</span>,
      meta: { align: 'right' },
      cell: ({ row }) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="icon" aria-label={`Editar ${row.original.name}`} onClick={() => { setEditing(row.original); setOpen(true); }}>
            <Pencil aria-hidden />
          </Button>
          <ConfirmDialog
            trigger={
              <Button variant="ghost" size="icon" aria-label={`Excluir ${row.original.name}`}>
                <Trash2 className="text-destructive" aria-hidden />
              </Button>
            }
            title={`Excluir "${row.original.name}"?`}
            description="O plano deixa de ser vendido. Assinaturas já criadas continuam valendo."
            confirmLabel="Excluir plano"
            onConfirm={() => remove(row.original)}
          />
        </div>
      ),
    },
  ];
  const create = (
    <Button onClick={() => { setEditing(null); setOpen(true); }}>
      <Plus aria-hidden /> Novo plano
    </Button>
  );
  return (
    <>
      <DataTable
        columns={columns}
        data={list.data}
        params={params}
        isLoading={list.isLoading}
        error={list.error}
        onRetry={() => list.refetch()}
        searchPlaceholder="Buscar plano"
        toolbar={create}
        empty={{ title: 'Nenhum plano ou pacote', description: 'Crie, por exemplo, um pacote de 4 cortes ou uma mensalidade.', action: create }}
      />
      <PlanModal open={open} onOpenChange={setOpen} plan={editing} />
    </>
  );
}
